/**
 * ActivityBar rail sizing: magnetic two-stop, nothing in between.
 *
 *   collapsed  (min, 60)     icon only
 *   labeled    (label, 162)  icon + title — magnetic expand target
 *
 * A small pull past the origin jumps between min and label.
 */

export type RailMode = 'collapsed' | 'expanded';

/** User-committed rail stop. Neither stop takes leftover window space. */
export type RailIdleCommit = 'collapsed' | 'labeled';

/** Magnetic expand target (icon + title stop). Figma `5154:167984` width. */
export const RAIL_LABEL_SIZE = 162;

/** Pointer delta (px) from gesture origin that expands a collapsed rail. */
export const RAIL_EXPAND_PULL = 12;

/** Extra pull (px) past the label edge that collapses an expanded rail. */
export const RAIL_COLLAPSE_PULL = 12;

/**
 * Duration of the 60↔162 magnetic `setSize` tween.
 * telegram-tt `--layer-transition` is 300ms; the ease-out cubic in
 * {@link magneticTweenWidth} matches `cubic-bezier(0.33, 1, 0.68, 1)`.
 */
export const RAIL_MAGNETIC_ANIM_MS = 300;

/**
 * Collapse hide threshold. Expand still paints the title as soon as width
 * leaves the icon stop; a shrinking rail drops it here so the title does
 * not linger through the whole 162→60 tween.
 */
export const RAIL_TITLE_HIDE_SIZE = 110;

export function railModeFromWidth(
  width: number,
  labelSize: number = RAIL_LABEL_SIZE,
): RailMode {
  return width >= labelSize ? 'expanded' : 'collapsed';
}

/**
 * Whether the rail should paint icon + title.
 *
 * Magnetic sizing still snaps to 60 / 162; presentation follows live width
 * with hysteresis: a growing rail shows once it leaves `collapsedSize`, a
 * shrinking rail hides once it falls below {@link RAIL_TITLE_HIDE_SIZE}.
 */
export function railShowsTitle(
  width: number,
  collapsedSize: number,
  previousWidth?: number,
  hideSize: number = RAIL_TITLE_HIDE_SIZE,
): boolean {
  if (previousWidth !== undefined && width < previousWidth) {
    return width >= hideSize;
  }
  return width > collapsedSize;
}

/** Ease-out cubic width between two magnetic stops. */
export function magneticTweenWidth(
  from: number,
  to: number,
  elapsedMs: number,
  durationMs: number = RAIL_MAGNETIC_ANIM_MS,
): number {
  if (durationMs <= 0 || elapsedMs >= durationMs) {
    return to;
  }
  if (elapsedMs <= 0) {
    return from;
  }
  const eased = 1 - (1 - elapsedMs / durationMs) ** 3;
  return from + (to - from) * eased;
}

export function railSizeForMode(
  mode: RailMode,
  min: number,
  labelSize: number = RAIL_LABEL_SIZE,
): number {
  return mode === 'expanded' ? labelSize : min;
}

/**
 * Restore leftover widths to the nearer stop. A stored width above the label
 * (from the retired free-resize zone) lands on the label.
 */
export function snapRestoredRailWidth(
  width: number,
  min: number,
  labelSize: number,
): number {
  return width >= (min + labelSize) / 2 ? labelSize : min;
}

/** Restore / first paint: derive the idle stop from a snapped width. */
export function railIdleCommitFromWidth(
  width: number,
  labelSize: number = RAIL_LABEL_SIZE,
): RailIdleCommit {
  return width >= labelSize ? 'labeled' : 'collapsed';
}

/** Sash end: the gesture phase is the source of truth, not a later layout width. */
export function railIdleCommitFromGesture(phase: RailMode): RailIdleCommit {
  return phase === 'collapsed' ? 'collapsed' : 'labeled';
}

/**
 * Idle min/max after a sash or restore: `min==max` on the committed stop so
 * a window resize cannot pull the rail off it (the trailing sash keeps an
 * `ew-resize` cursor in CSS).
 */
export function railIdleConstraints(
  commit: RailIdleCommit,
  min: number,
  labelSize: number,
): { min: number; max: number } {
  const stop = railIdlePinWidth(commit, min, labelSize);
  return { min: stop, max: stop };
}

/** Width of the committed stop. */
export function railIdlePinWidth(
  commit: RailIdleCommit,
  min: number,
  labelSize: number,
): number {
  return commit === 'collapsed' ? min : labelSize;
}

export interface RailDragState {
  readonly phase: RailMode;
  /** Pointer origin for the current phase. */
  readonly anchorX: number;
  /** Stop width while in this phase: `min` collapsed, `label` expanded. */
  readonly width: number;
}

export function beginRailDrag(
  pointerX: number,
  startWidth: number,
  min: number,
  labelSize: number = RAIL_LABEL_SIZE,
): RailDragState {
  return startWidth < labelSize
    ? { phase: 'collapsed', anchorX: pointerX, width: min }
    : { phase: 'expanded', anchorX: pointerX, width: labelSize };
}

/**
 * Advance the platform-owned sash gesture.
 *
 * Crossing the pull threshold jumps to the other stop, and the pointer
 * position at that instant becomes the new origin, so one uninterrupted
 * gesture can go back and forth. Between jumps the width stays on its stop.
 */
export function updateRailDrag(
  state: RailDragState,
  pointerX: number,
  min: number,
  labelSize: number,
  expandPull: number = RAIL_EXPAND_PULL,
  collapsePull: number = RAIL_COLLAPSE_PULL,
): RailDragState {
  const dx = pointerX - state.anchorX;
  if (state.phase === 'collapsed') {
    return dx < expandPull
      ? state
      : { phase: 'expanded', anchorX: pointerX, width: labelSize };
  }
  return dx > -collapsePull
    ? state
    : { phase: 'collapsed', anchorX: pointerX, width: min };
}

/**
 * Next magnetic stop for a sash gesture.
 *
 * - Started collapsed (`startWidth < labelSize`): expand when `dx` crosses
 *   `expandPull`; collapse again only after dragging back past the origin.
 * - Started on the label stop: collapse once `dx` pulls `collapsePull` left.
 */
export function nextRailMode(
  mode: RailMode,
  dx: number,
  startWidth: number,
  labelSize: number = RAIL_LABEL_SIZE,
  expandPull: number = RAIL_EXPAND_PULL,
  collapsePull: number = RAIL_COLLAPSE_PULL,
): RailMode {
  if (startWidth < labelSize) {
    if (dx >= expandPull) {
      return 'expanded';
    }
    if (dx <= -collapsePull) {
      return 'collapsed';
    }
    return mode;
  }
  if (startWidth + dx <= labelSize - collapsePull) {
    return 'collapsed';
  }
  return 'expanded';
}
